import BaseMapLeafletController from './map_leaflet/base_controller';
import { MapInitialization } from './map_leaflet/map_initialization';
import { LayerManagement } from './map_leaflet/layer_management';
import { MarkerManagement } from './map_leaflet/marker_management';
import { DataHandling } from './map_leaflet/data_handling';
import { UIComponents } from './map_leaflet/ui_components';
import { ProviderFiltering } from './map_leaflet/provider_filtering';
import Logger from '../lib/logger';
/**
 * MapLeafletController - Enhanced Leaflet Map Integration for ERP System
 *
 * This Stimulus controller provides comprehensive map functionality including:
 * - Interactive Leaflet maps with custom markers and popups
 * - Dynamic data loading with area-based filtering
 * - KML/TopoJSON layer support with toggle functionality
 * - Responsive UI with theme compatibility
 * - Performance optimizations and error handling
 *
 * Key Features:
 * - Debounced area loading for performance
 * - Retry logic for network requests
 * - Memory leak prevention with proper cleanup
 * - Theme-aware styling and components
 * - Comprehensive error handling and user feedback
 *
 * Data Attributes:
 * - data-map-leaflet-kml-value: KML file URL to load
 * - data-map-leaflet-url-value: Base URL for data fetching
 * - data-lat: Latitude for initial marker
 * - data-long: Longitude for initial marker
 * - data-state: State ID for TopoJSON loading
 * - data-topojson: TopoJSON data URL
 *
 * Targets:
 * - mapdiv: Main map container element
 * - infodiv: Information display container (optional)
 * - spinner: Loading spinner element (optional)
 *
 * @class MapLeafletController
 * @extends {BaseMapLeafletController}
 */
export default class MapLeafletController extends BaseMapLeafletController {
    connect() {
        // Call parent connect method first to initialize controller state
        super.connect();
        // Initialize modules after controller is fully initialized
        this.mapInit = new MapInitialization(this);
        this.layerMgmt = new LayerManagement(this);
        this.markerMgmt = new MarkerManagement(this);
        this.dataHandling = new DataHandling(this);
        this.uiComponents = new UIComponents(this);
        this.providerFiltering = new ProviderFiltering(this);
        // Setup keyboard shortcuts
        this.setupKeyboardShortcuts();
        // Now initialize the map and setup data sources
        try {
            Logger.debug('Initializing MapLeafletController');
            this.initMap();
            this.setupDataSources();
            Logger.success('MapLeafletController connected and ready', { id: this.element.id });
        }
        catch (error) {
            Logger.error('Failed to initialize map controller', error, {
                controller: this.constructor.name,
                modules: {
                    mapInit: !!this.mapInit,
                    layerMgmt: !!this.layerMgmt,
                    markerMgmt: !!this.markerMgmt,
                    dataHandling: !!this.dataHandling,
                    uiComponents: !!this.uiComponents,
                    providerFiltering: !!this.providerFiltering
                }
            });
            this.showErrorMessage('Failed to initialize map. Please refresh the page.');
        }
    }
    disconnect() {
        // Clean up keyboard shortcuts
        this.cleanupKeyboardShortcuts();
        super.disconnect();
    }
    // Setup keyboard shortcuts for common map actions
    setupKeyboardShortcuts() {
        this.keyboardHandler = this.handleKeyboardShortcut.bind(this);
        document.addEventListener('keydown', this.keyboardHandler);
    }
    // Clean up keyboard shortcuts
    cleanupKeyboardShortcuts() {
        if (this.keyboardHandler) {
            document.removeEventListener('keydown', this.keyboardHandler);
            this.keyboardHandler = null;
        }
    }
    // Handle keyboard shortcuts
    handleKeyboardShortcut(event) {
        // Don't trigger shortcuts if user is typing in an input/textarea
        const target = event.target;
        if (target.tagName === 'INPUT' ||
            target.tagName === 'TEXTAREA' ||
            target.isContentEditable) {
            return;
        }
        // Handle shortcuts (case-insensitive)
        const key = event.key.toLowerCase();
        switch (key) {
            case 'b':
                // Toggle Bids layer
                event.preventDefault();
                const bidsButton = this.hasBidsButtonTarget ? this.bidsButtonTarget : null;
                if (bidsButton) {
                    const isActive = this.map && this.bids && this.map.hasLayer(this.bids);
                    bidsButton.click();
                    const message = isActive ? 'Bids layer hidden' : 'Bids layer displayed';
                    this.showInfoMessage(message, 'info');
                    this.announceToScreenReader(message);
                }
                break;
            case 'g':
                // Toggle Coverage Map (GeoJSON)
                event.preventDefault();
                const geoButton = this.hasGeoButtonTarget ? this.geoButtonTarget : null;
                if (geoButton) {
                    const isActive = this.map && this.geojson && this.map.hasLayer(this.geojson);
                    geoButton.click();
                    const message = isActive ? 'Coverage map hidden' : 'Coverage map displayed';
                    this.showInfoMessage(message, 'info');
                    this.announceToScreenReader(message);
                }
                break;
            case 'c':
                // Center map on address
                event.preventDefault();
                this.center(event);
                const message = 'Map centered on address';
                this.showInfoMessage(message, 'info');
                this.announceToScreenReader(message);
                break;
            case 'escape':
                // Close map
                event.preventDefault();
                // Use Stimulus target if available, fallback to querySelector
                const closeButton = this.hasCloseMapButtonTarget
                    ? this.closeMapButtonTarget
                    : null;
                if (closeButton) {
                    closeButton.click();
                    this.announceToScreenReader('Map closed');
                }
                break;
            case 'p':
                // Switch to Bid Providers tab
                event.preventDefault();
                // Use Stimulus target if available, fallback to getElementById
                const bidProvidersTab = this.hasBidProvidersTabTarget
                    ? this.bidProvidersTabTarget
                    : null;
                if (bidProvidersTab) {
                    // Use Bootstrap's tab functionality if available, otherwise simulate click
                    if (typeof bootstrap !== 'undefined' && bootstrap.Tab) {
                        const tabTrigger = new bootstrap.Tab(bidProvidersTab);
                        tabTrigger.show();
                    }
                    else {
                        // Fallback: simulate a click on the tab button
                        bidProvidersTab.click();
                    }
                    // Load the content after tab is shown
                    setTimeout(() => {
                        this.uiComponents.loadBidProviders(event);
                    }, 100);
                    const providersMessage = 'Switched to Bid Providers tab';
                    this.showInfoMessage(providersMessage, 'info');
                    this.announceToScreenReader(providersMessage);
                }
                break;
            case 'l':
                // Switch to Bid List tab
                event.preventDefault();
                // Use Stimulus target if available, fallback to getElementById
                const bidListTab = this.hasBidsTabTarget
                    ? this.bidsTabTarget
                    : null;
                if (bidListTab) {
                    // Use Bootstrap's tab functionality if available, otherwise simulate click
                    if (typeof bootstrap !== 'undefined' && bootstrap.Tab) {
                        const tabTrigger = new bootstrap.Tab(bidListTab);
                        tabTrigger.show();
                    }
                    else {
                        // Fallback: simulate a click on the tab button
                        bidListTab.click();
                    }
                    // Load the content after tab is shown
                    setTimeout(() => {
                        this.uiComponents.loadBidList(event);
                    }, 100);
                    const listMessage = 'Switched to Bid List tab';
                    this.showInfoMessage(listMessage, 'info');
                    this.announceToScreenReader(listMessage);
                }
                break;
            case 'r':
                // Toggle Recent bids switch
                event.preventDefault();
                // Toggle the value and trigger the change handler
                this.showRecentBidsOnlyValue = !this.showRecentBidsOnlyValue;
                const isChecked = this.showRecentBidsOnlyValue;
                const recentMessage = isChecked ? 'Recent bids filter enabled' : 'Recent bids filter disabled';
                this.showInfoMessage(recentMessage, 'info');
                this.announceToScreenReader(recentMessage);
                break;
            case 'k':
                // Toggle all Layers
                event.preventDefault();
                // Use Stimulus target if available, fallback to getElementById
                const toggleAllLayersButton = this.hasToggleAllKmlsTarget
                    ? this.toggleAllKmlsTarget
                    : null;
                if (toggleAllLayersButton) {
                    // Trigger a click event on the checkbox to activate the Stimulus action
                    toggleAllLayersButton.click();
                    const isChecked = toggleAllLayersButton.checked;
                    const layersMessage = isChecked ? 'All layers enabled' : 'All layers disabled';
                    this.showInfoMessage(layersMessage, 'info');
                    this.announceToScreenReader(layersMessage);
                }
                break;
            case '?':
                // Show keyboard shortcuts help
                event.preventDefault();
                this.showKeyboardShortcutsHelp();
                this.announceToScreenReader('Keyboard shortcuts help displayed');
                break;
        }
    }
    // Show keyboard shortcuts help dialog
    showKeyboardShortcutsHelp() {
        // Using theme wrapper classes for dark mode compatibility
        const helpHtml = `
      <div class="keyboard-shortcuts-help">
        <h5 class="mb-3"><i class="fas fa-keyboard me-2"></i>Keyboard Shortcuts</h5>
        <table class="theme-table table-sm table-borderless">
          <tbody>
            <tr>
              <td class="pe-3"><kbd class="bg-secondary text-light px-2 py-1 rounded">B</kbd></td>
              <td>Toggle bids markers</td>
            </tr>
            <tr>
              <td class="pe-3"><kbd class="bg-secondary text-light px-2 py-1 rounded">G</kbd></td>
              <td>Toggle coverage map layer</td>
            </tr>
            <tr>
              <td class="pe-3"><kbd class="bg-secondary text-light px-2 py-1 rounded">C</kbd></td>
              <td>Center map on address</td>
            </tr>
            <tr>
              <td class="pe-3"><kbd class="bg-secondary text-light px-2 py-1 rounded">P</kbd></td>
              <td>Switch to Bid Providers tab</td>
            </tr>
            <tr>
              <td class="pe-3"><kbd class="bg-secondary text-light px-2 py-1 rounded">L</kbd></td>
              <td>Switch to Bid List tab</td>
            </tr>
            <tr>
              <td class="pe-3"><kbd class="bg-secondary text-light px-2 py-1 rounded">R</kbd></td>
              <td>Toggle Recent bids filter</td>
            </tr>
            <tr>
              <td class="pe-3"><kbd class="bg-secondary text-light px-2 py-1 rounded">K</kbd></td>
              <td>Toggle all layers</td>
            </tr>
            <tr>
              <td class="pe-3"><kbd class="bg-secondary text-light px-2 py-1 rounded">ESC</kbd></td>
              <td>Close map view</td>
            </tr>
            <tr>
              <td class="pe-3"><kbd class="bg-secondary text-light px-2 py-1 rounded">?</kbd></td>
              <td>Show this help</td>
            </tr>
          </tbody>
        </table>
        <p class="text-body-secondary small mb-0">
          <i class="fas fa-info-circle me-1"></i>
          Shortcuts are disabled when typing in input fields
        </p>
      </div>
    `;
        this.showInfoMessage(helpHtml, 'info');
    }
    // Override initMap to use module
    initMap() {
        this.mapInit.initMap();
    }
    // Override areaLoad to use module
    areaLoad(e) {
        return this.dataHandling.areaLoad(e);
    }
    // Override loadkml to use module
    loadkml() {
        return this.layerMgmt.loadkml();
    }
    // Override getLocationData to use module
    getLocationData(stateInfoUrl, stateFileUrl) {
        return this.dataHandling.getLocationData(stateInfoUrl, stateFileUrl);
    }
    // Override handleGeoZoomEnd to use module
    handleGeoZoomEnd(e) {
        return this.layerMgmt.handleGeoZoomEnd(e);
    }
    // Map initialization methods
    center(event) {
        return this.mapInit.center(event);
    }
    clear(event) {
        return this.mapInit.clear(event);
    }
    // Layer management methods
    toggleGeo(event) {
        return this.layerMgmt.toggleGeo(event);
    }
    addKml(event) {
        return this.layerMgmt.addKml(event);
    }
    toggleAllKml(event) {
        return this.layerMgmt.toggleAllKml(event);
    }
    toggleKml(event) {
        return this.layerMgmt.toggleKml(event);
    }
    showbids(event) {
        return this.layerMgmt.showbids(event);
    }
    // Data handling methods
    handleZoomEnd(e) {
        return this.dataHandling.handleZoomEnd(e);
    }
    handleMoveEnd(e) {
        return this.dataHandling.handleMoveEnd(e);
    }
    fetchWithRetry(url, options = {}, maxRetries = 3) {
        return this.dataHandling.fetchWithRetry(url, options, maxRetries);
    }
    parseResponseData(json) {
        return this.dataHandling.parseResponseData(json);
    }
    handleResponseMeta(meta) {
        return this.dataHandling.handleResponseMeta(meta);
    }
    reloadMarkersWithFilter(providerId) {
        return this.dataHandling.reloadMarkersWithFilter(providerId);
    }
    extractBidProvidersFromMarkers() {
        return this.dataHandling.extractBidProvidersFromMarkers();
    }
    // Marker management methods
    addBids(jsonData) {
        return this.markerMgmt.addBids(jsonData);
    }
    createBidMarker(v, icon, popupHtml) {
        return this.markerMgmt.createBidMarker(v, icon, popupHtml);
    }
    createBidIcons() {
        return this.markerMgmt.createBidIcons();
    }
    determineMarkerType(address, icons) {
        return this.markerMgmt.determineMarkerType(address, icons);
    }
    createEnhancedPopup(addressData, status, statusText) {
        return this.markerMgmt.createEnhancedPopup(addressData, status, statusText);
    }
    getStatusClass(status) {
        return this.markerMgmt.getStatusClass(status);
    }
    getStatusIcon(status) {
        return this.markerMgmt.getStatusIcon(status);
    }
    centerOnLocation(event) {
        return this.markerMgmt.centerOnLocation(event);
    }
    formatAddressForDisplay(address) {
        return this.markerMgmt.formatAddressForDisplay(address);
    }
    // UI components methods
    showSpinner(delay = 250) {
        return this.uiComponents.showSpinner(delay);
    }
    hideSpinner() {
        return this.uiComponents.hideSpinner();
    }
    showBidProvidersLoading() {
        return this.uiComponents.showBidProvidersLoading();
    }
    updateBidProvidersContent(html) {
        return this.uiComponents.updateBidProvidersContent(html);
    }
    updateBidCount(count) {
        return this.uiComponents.updateBidCount(count);
    }
    loadBidProviders(event, options = {}) {
        return this.uiComponents.loadBidProviders(event, options);
    }
    loadBidList(event, options = {}) {
        return this.uiComponents.loadBidList(event, options);
    }
    generateBidProvidersHtml(bidProviders) {
        return this.uiComponents.generateBidProvidersHtml(bidProviders);
    }
    generateProviderCardHtml(provider) {
        return this.uiComponents.generateProviderCardHtml(provider);
    }
    buildBidsCrmUrl(providerId) {
        return this.uiComponents.buildBidsCrmUrl(providerId);
    }
    autoUpdateBidProvidersTab() {
        return this.uiComponents.autoUpdateBidProvidersTab();
    }
    autoUpdateBidListTab() {
        return this.uiComponents.autoUpdateBidListTab();
    }
    toggleDistanceSort(event) {
        return this.uiComponents.toggleDistanceSort(event);
    }
    // Provider filtering methods
    filterMarkersByProvider(event) {
        return this.providerFiltering.filterMarkersByProvider(event);
    }
    clearProviderFilter(event) {
        return this.providerFiltering.clearProviderFilter(event);
    }
    updateFilterStatus() {
        return this.providerFiltering.updateFilterStatus();
    }
    extractProviderFromPopup(popupHtml, latLng) {
        return this.providerFiltering.extractProviderFromPopup(popupHtml, latLng);
    }
    // Utility methods
    formatBoundsString(bounds) {
        return this.dataHandling.formatBoundsString(bounds);
    }
    getProviderColor(providerCount) {
        return this.dataHandling.getProviderColor(providerCount);
    }
    getColors(d) {
        return this.dataHandling.getColors(d);
    }
    ensureProperLayerOrdering() {
        return this.mapInit.ensureProperLayerOrdering();
    }
    // Additional delegated methods
    refreshGeoJSONLayer() {
        return this.layerMgmt.refreshGeoJSONLayer();
    }
    // Handle recent bids filter change (called by value callback)
    async handle_recent_bids_filter_change_guarded(value) {
        if (this.isFiltering)
            return;
        this.isFiltering = true;
        // Get current toggle targets to disable/enable
        const toggleCheckboxes = this.recentBidsCheckboxTargets;
        toggleCheckboxes.forEach(cb => cb.disabled = true);
        try {
            // PERFORMANCE: Clear cache when filter changes
            this.layerMgmt.clearCache();
            // Get fresh signal and cancel pending requests
            const signal = this.getAbortSignal();
            // Only reload if bids layer is currently visible
            if (this.bids && this.map.hasLayer(this.bids)) {
                this.showSpinner();
                // Get current bounds
                const bounds = this.map.getBounds();
                const area = this.dataHandling.formatBoundsString(bounds);
                // Build URL using base URL from data attribute
                let url = `${this.dataHandling.getBaseUrl()}?l=${area}`;
                // Apply provider filter if active
                const currentFilter = this.getCurrentProviderFilter();
                if (currentFilter) {
                    url += `&provider_id=${currentFilter.id}`;
                }
                // Add show_all parameter if showing all bids
                if (!value) {
                    url += '&show_all=true';
                }
                // Fetch and update markers
                const data = await this.fetchWithRetry(url, { signal });
                const { addresses, meta } = this.parseResponseData(data);
                this.handleResponseMeta(meta);
                // Clear and re-add markers
                if (this.map.hasLayer(this.bids)) {
                    this.map.removeLayer(this.bids);
                }
                this.addBids(addresses);
                if (!this.map.hasLayer(this.bids)) {
                    this.bids.addTo(this.map);
                    this.ensureProperLayerOrdering();
                }
                // IMPORTANT: Reset tracking variables so next zoom/move will fetch new data
                this.previousZoom = this.map.getZoom();
                this.previousCenter = this.map.getCenter();
                this.previousBounds = this.map.getBounds();
                // Show feedback message
                const filterText = value ? 'recent bids (last 6 months)' : 'all bids';
                this.showInfoMessage(`Map updated to show ${filterText}`, 'info');
                this.hideSpinner();
                // Update bid providers list (silent background update)
                await this.loadBidProviders(null, { silent: true });
                // Update bid list tab (silent background update)
                await this.loadBidList(null, { silent: true });
            }
        }
        catch (error) {
            if (error.name === 'AbortError') {
                Logger.debug('Filter change request aborted');
                return;
            }
            Logger.error('Failed to handle recent bids filter change', error, {
                controller: this.constructor.name,
                value
            });
            this.showErrorMessage('Failed to update map filter. Please try again.');
            this.hideSpinner();
        }
        finally {
            this.isFiltering = false;
            toggleCheckboxes.forEach(cb => cb.disabled = false);
        }
    }
    // Toggle recent bids only filter (action method)
    toggleRecentBids(event) {
        // Update value (will trigger valueChanged callback)
        this.showRecentBidsOnlyValue = event.target.checked;
    }
    // Value change callback for recent bids filter
    showRecentBidsOnlyValueChanged(value, previousValue) {
        // Skip on initial load (when previousValue is undefined)
        if (typeof previousValue === 'undefined')
            return;
        this.handle_recent_bids_filter_change_guarded(value);
    }
    // Value change callback for hover popup behavior
    showPopupOnHoverValueChanged(value, previousValue) {
        // Skip on initial load
        if (previousValue === undefined)
            return;
        this.handlePopupHoverChange(value);
    }
    // Toggle hover popup behavior (action method)
    toggleHoverPopup(event) {
        // Update value (will trigger valueChanged callback)
        this.showPopupOnHoverValue = event.target.checked;
    }
    // Handle popup hover change (called by value callback)
    handlePopupHoverChange(value) {
        try {
            // Recreate markers if bids layer exists and is visible
            if (this.bids && this.map.hasLayer(this.bids) && this.currentBidAddresses) {
                // Remove current layer
                this.map.removeLayer(this.bids);
                // Recreate markers with new behavior
                this.bids.clearLayers();
                this.addBids(this.currentBidAddresses);
                // Add back to map
                this.bids.addTo(this.map);
                this.ensureProperLayerOrdering();
                const message = value ?
                    'Popups will now show on hover' :
                    'Popups will now show on click only';
                this.showInfoMessage(message, 'info');
            }
        }
        catch (error) {
            Logger.error('Failed to handle popup hover change', error, {
                controller: this.constructor.name,
                value
            });
            this.showErrorMessage('Failed to update popup behavior. Please try again.');
        }
    }
    // Toggle GeoJSON layer with button
    toggleGeoButton(event) {
        event.preventDefault();
        event.stopPropagation();
        const button = event.currentTarget;
        const isActive = this.map.hasLayer(this.geojson);
        // Call the existing toggleGeo logic
        this.toggleGeo(event);
        // Toggle button classes based on new state (inverse of previous state)
        if (isActive) {
            // Layer was removed, so button should be outline (inactive)
            button.classList.remove('btn-warning');
            button.classList.add('btn-outline-warning');
            if (this.hasInactiveButtonClass) {
                button.classList.add(this.inactiveButtonClass);
            }
        }
        else {
            // Layer was added, so button should be solid (active)
            button.classList.remove('btn-outline-warning');
            button.classList.add('btn-warning');
            if (this.hasActiveButtonClass) {
                button.classList.add(this.activeButtonClass);
            }
        }
    }
    // Update bids button state to match layer visibility
    updateBidsButtonState() {
        // Use Stimulus target if available, fallback to getElementById
        const button = this.hasBidsButtonTarget
            ? this.bidsButtonTarget
            : null;
        if (!button)
            return;
        const isActive = this.bids && this.map && this.map.hasLayer(this.bids);
        if (isActive) {
            // Layer is on map, so button should be solid (active)
            button.classList.remove('btn-outline-primary');
            button.classList.add('btn-primary');
        }
        else {
            // Layer is not on map, so button should be outline (inactive)
            button.classList.remove('btn-primary');
            button.classList.add('btn-outline-primary');
        }
    }
    // Toggle bids layer with button
    async toggleBidsButton(event) {
        event.preventDefault();
        event.stopPropagation();
        // Call the existing showbids logic
        await this.showbids(event);
        // Update button state to match new layer state
        this.updateBidsButtonState();
    }
    // Event Handlers for decoupled communication
    handleInfiniteScrollLoaded(event) {
        this.uiComponents.handleInfiniteScrollLoaded(event);
    }
    // Activate Bid List tab from external event (like marker popup click)
    activateBidListTab(event) {
        this.uiComponents.activateBidListTab();
    }
}
